import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { getProjects } from '../services/apiService';
import { ProjectFA } from '../interfaces/global-interfaces';
import { useOrganization } from './OrganizationContext';

type ProjectContextType = {
    projects: ProjectFA[];
    selectedProjectId: string;
    setSelectedProjectId: (id: string) => void;
    selectedProject: ProjectFA | null;
    setSelectedProject: (project: ProjectFA | null) => void;
    setProjects: (projects: ProjectFA[]) => void;
};

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
    const [projects, setProjects] = useState<ProjectFA[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [selectedProject, setSelectedProject] = useState<ProjectFA | null>(null); // 🔹 accetta anche null

    const { selectedOrganizationId } = useOrganization();
    const previousOrganizationId = useRef<string>('');

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                if (!selectedOrganizationId) return;

                // Selecting a different organization invalidates whatever project was
                // previously selected, so we shouldn't keep looking for its id here.
                const organizationChanged = previousOrganizationId.current !== selectedOrganizationId;
                previousOrganizationId.current = selectedOrganizationId;
                const activeProjectId = organizationChanged ? '' : selectedProjectId;

                const data = await getProjects(selectedOrganizationId);
                setProjects(data);

                if (activeProjectId) {
                    const project = data.find(p => p._id === activeProjectId) || null;
                    setSelectedProject(project);
                } else if (data.length > 0) {
                    setSelectedProjectId(data[0]._id!);
                    setSelectedProject(data[0]);
                } else {
                    setSelectedProjectId('');
                    setSelectedProject(null);
                }
            } catch (error) {
                console.error("Errore nel caricamento dei progetti:", error);
            }
        };

        void fetchProjects();
    }, [selectedOrganizationId, selectedProjectId]);

    return (
        <ProjectContext.Provider
            value={{
                projects,
                selectedProjectId,
                setSelectedProjectId,
                selectedProject,
                setSelectedProject,
                setProjects
            }}
        >
            {children}
        </ProjectContext.Provider>
    );
};

export const useProject = (): ProjectContextType => {
    const context = useContext(ProjectContext);
    if (!context) throw new Error('useProject must be used within a ProjectProvider');
    return context;
};

